// axios 实例 + 拦截器：自动带管理员 Bearer token，解包 { code, msg, data }，统一错误与 401 处理。
import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios'
import { ElMessage } from 'element-plus'
import type { ApiResult } from './types'

const TOKEN_KEY = 'admin_token'

export const getToken = (): string => localStorage.getItem(TOKEN_KEY) ?? ''
export const setToken = (t: string): void => localStorage.setItem(TOKEN_KEY, t)
export const clearToken = (): void => localStorage.removeItem(TOKEN_KEY)

/** 业务错误（后端 code !== 0 时抛出）。 */
export class ApiError extends Error {
  code: number
  constructor(code: number, msg: string) {
    super(msg)
    this.code = code
    this.name = 'ApiError'
  }
}

const instance: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE,
  timeout: 15000,
})

// 请求拦截：附带管理员 token。
instance.interceptors.request.use((config) => {
  const token = getToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// 响应拦截：解包业务包。成功返回 data；失败弹提示并 reject。
instance.interceptors.response.use(
  (response) => {
    const body = response.data as ApiResult
    if (body && typeof body.code === 'number') {
      if (body.code === 0) return body.data
      ElMessage.error(body.msg || `请求失败（code ${body.code}）`)
      return Promise.reject(new ApiError(body.code, body.msg))
    }
    // 非业务包格式（极少数情况），原样返回。
    return response.data
  },
  (error) => {
    const status = error?.response?.status
    if (status === 401) {
      clearToken()
      // hash 路由下跳转到登录页，并记录来源便于回跳。
      if (!location.hash.startsWith('#/login')) {
        const redirect = encodeURIComponent(location.hash.slice(1) || '/')
        location.hash = `#/login?redirect=${redirect}`
      }
      ElMessage.error('登录已过期，请重新登录')
    } else {
      const msg =
        (error?.response?.data as ApiResult | undefined)?.msg || error?.message || '网络错误'
      ElMessage.error(msg)
    }
    return Promise.reject(error)
  },
)

// 泛型包装：让调用方直接拿到解包后的 data（而非 AxiosResponse）。
export function get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
  return instance.get(url, config) as unknown as Promise<T>
}

export function post<T = unknown>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  return instance.post(url, data, config) as unknown as Promise<T>
}

export default instance
